import React, { useEffect, useState } from 'react';
import { NavLink, Route, Switch, useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import '../ep.css';
import { showError } from '../api';
import { COMPETITIVENESS, md } from '../format';
import { useLoad } from '../hooks';
import { clearAuthData, removeAccessToken, removeUserId } from 'utils/sessionStorage';
import { partnerApi, PartnerMe, PartnerProgramRow, StatRow, TeamRow } from './api';
import ProgramEditor from './ProgramEditor';

/**
 * Partner portal for program providers (user_role 'provider'). Their own small
 * app at /partners: your programs, the editor, stats and team. Nothing here
 * shows a student's name, email or phone.
 */
export default function PartnerPortal() {
  const me = useLoad<PartnerMe>(() => partnerApi.me(), []);
  // Intercom chat: tell the team which organization a partner belongs to.
  useEffect(() => {
    const org = me.data?.org;
    const w = window as any;
    if (!org || typeof w.Intercom !== 'function') return;
    w.Intercom('update', {
      user_type: 'provider',
      partner_org: org.name,
      company: { company_id: `partner-org-${Number(String(org.id).replace(/\D/g, ''))}`, name: org.name, website: org.website ?? undefined },
    });
  }, [me.data]);
  const history = useHistory();
  const signOut = () => {
    clearAuthData();
    removeAccessToken();
    removeUserId();
    history.push('/public/login');
  };
  if (me.error) {
    return (
      <div className="ep">
        <div className="card empty" style={{ maxWidth: 560, margin: '40px auto' }}>
          <h2>This account is not a partner account</h2>
          <p style={{ marginTop: 8 }}>The partner portal is for organizations that list programs on GradMap.</p>
          <button type="button" className="btn" style={{ marginTop: 14 }} onClick={() => history.push('/dashboard')}>Go to GradMap</button>
        </div>
      </div>
    );
  }
  return (
    <div className="ep" style={{ padding: 0 }}>
      <header className="ep-pheader">
        <span className="ep-logo">gradmap</span>
        <span className="muted" style={{ fontSize: 17 }}>Partner portal</span>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 500 }}>{me.data?.org.name ?? ''}</div>
          <button type="button" className="linkish small" onClick={signOut}>Sign out</button>
        </div>
      </header>
      <nav className="ep-pnav" aria-label="Partner portal">
        <NavLink to="/partners" className="chipb" activeClassName="on" isActive={(_m, l) => l.pathname === '/partners' || l.pathname.startsWith('/partners/programs')}>Your programs</NavLink>
        <NavLink to="/partners/stats" className="chipb" activeClassName="on">Stats</NavLink>
        <NavLink to="/partners/team" className="chipb" activeClassName="on">Team</NavLink>
      </nav>
      <div style={{ padding: '8px 24px 64px', maxWidth: 1200, margin: '0 auto' }}>
        {me.data && (
          <Switch>
            <Route exact path="/partners" render={() => <ProgramsHome me={me.data!} reloadMe={me.reload} />} />
            <Route path="/partners/programs/:id" render={(p) => <ProgramEditor id={p.match.params.id} me={me.data!} onSaved={me.reload} />} />
            <Route path="/partners/stats" component={StatsPage} />
            <Route path="/partners/team" render={() => <TeamPage me={me.data!} />} />
          </Switch>
        )}
      </div>
    </div>
  );
}

const statusChip = (r: PartnerProgramRow, needs: boolean) => {
  if (needs) return <span className="chip w">GradMap needs a few things</span>;
  if (r.last_update_status === 'pending') return <span className="chip a">Waiting for GradMap</span>;
  if (r.program_status === 'not_running_this_year') return <span className="chip">Not running this year</span>;
  if (r.program_status === 'ended') return <span className="chip">Ended</span>;
  return r.published ? <span className="chip ok">Live</span> : <span className="chip">Not live</span>;
};

function ProgramsHome({ me, reloadMe }: { me: PartnerMe; reloadMe: () => void }) {
  const history = useHistory();
  const rows = useLoad<PartnerProgramRow[]>(() => partnerApi.programs(), []);
  const [claimOpen, setClaimOpen] = useState(false);
  const [reply, setReply] = useState<{ id: string; text: string } | null>(null);
  const needsBy = new Map(me.needs_you.map((n) => [n.program_id, n]));
  const nameOf = (pid: number) => rows.data?.find((r) => r.id === pid)?.name ?? `#${pid}`;

  return (
    <>
      <div className="between" style={{ margin: '16px 0', flexWrap: 'wrap' }}>
        <div>
          <h1>Your programs</h1>
          <p className="body" style={{ marginTop: 4 }}>{`${me.org.name} · ${rows.data?.length ?? 0} programs`}</p>
        </div>
        <div className="row">
          <button type="button" className="btn" onClick={() => setClaimOpen(true)}>Claim a listing</button>
          <button type="button" className="btn primary" onClick={() => history.push('/partners/programs/new')}>Add a program</button>
        </div>
      </div>

      {me.needs_you.map((n) => (
        <div key={n.id} className="panel" style={{ background: 'var(--gm-warning-wash, #fdf3e2)', marginBottom: 12 }}>
          <div className="between" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ color: 'var(--gm-warning-ink, #b45309)' }}>
              <b style={{ fontWeight: 500 }}>{`GradMap needs ${n.fields.length || 'a few'} ${n.fields.length === 1 ? 'thing' : 'things'} on ${nameOf(n.program_id)}`}</b>
              {n.message ? `: "${n.message}"` : ''}
            </div>
            <div className="row">
              <button type="button" className="btn sm" onClick={() => setReply({ id: n.id, text: '' })}>Reply</button>
              <button type="button" className="btn sm primary" onClick={() => history.push(`/partners/programs/${n.program_id}`)}>Fix now</button>
            </div>
          </div>
        </div>
      ))}

      <div className="panel between" style={{ marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          {me.publishes_directly
            ? 'Your changes publish right away. GradMap still looks at big changes, like a new website address.'
            : `GradMap checks your first 2 updates before they go live. ${me.reviewed_updates_left} left, then your changes publish right away.`}
        </span>
        {!me.publishes_directly && <span className="chip a">{`${me.reviewed_updates_left} reviewed ${me.reviewed_updates_left === 1 ? 'update' : 'updates'} left`}</span>}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="ep-tbl">
          <thead>
            <tr><th>GradMap #</th><th>Program</th><th>Sessions</th><th>Next deadline</th><th>How competitive</th><th>Status</th><th aria-label="Edit" /></tr>
          </thead>
          <tbody>
            {(rows.data ?? []).map((r) => (
              <tr key={r.id}>
                <td data-l="GradMap #" className="muted">{r.gradmap_number}</td>
                <td data-l="Program" style={{ fontWeight: 500 }}>{r.name}</td>
                <td data-l="Sessions">{r.sessions}</td>
                <td data-l="Next deadline">{r.next_deadline ? md(r.next_deadline) : 'Not posted yet'}</td>
                <td data-l="How competitive">{r.competitiveness_label ? <span className="chip">{COMPETITIVENESS[r.competitiveness_label]?.label}</span> : <span className="muted">Not set</span>}</td>
                <td data-l="Status">{statusChip(r, needsBy.has(r.id))}</td>
                <td><button type="button" className="btn sm" onClick={() => history.push(`/partners/programs/${r.id}`)}>Edit</button></td>
              </tr>
            ))}
            {rows.data && rows.data.length === 0 && (
              <tr><td colSpan={7} className="body">No programs yet. Claim a listing GradMap already has, or add a program.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {claimOpen && <ClaimModal onClose={() => setClaimOpen(false)} />}
      {reply && (
        <div className="ep-modal-bg" role="dialog" aria-modal="true" aria-labelledby="ep-rp" onClick={() => setReply(null)}>
          <div className="ep-modal ep" style={{ padding: 24, minHeight: 0 }} onClick={(e) => e.stopPropagation()}>
            <h2 id="ep-rp">Reply to GradMap</h2>
            <textarea className="in" autoFocus style={{ marginTop: 12 }} maxLength={4000} value={reply.text} onChange={(e) => setReply({ ...reply, text: e.target.value })} aria-label="Your reply" />
            <div className="foot">
              <button
                type="button"
                className="btn primary"
                disabled={!reply.text.trim()}
                onClick={async () => {
                  try {
                    await partnerApi.reply(reply.id, reply.text.trim());
                    setReply(null);
                    toast.success('Sent. It is back with GradMap.');
                    reloadMe();
                  } catch (e) {
                    showError(e);
                  }
                }}
              >
                Send reply
              </button>
              <button type="button" className="btn" onClick={() => setReply(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ClaimModal({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState<{ id: number; name: string; host: string | null; managed: boolean }[]>([]);
  const [note, setNote] = useState('');
  const search = async (v: string) => {
    setQ(v);
    if (v.trim().length >= 3) setFound(await partnerApi.listings(v.trim()).catch(() => []));
    else setFound([]);
  };
  const claim = async (id: number) => {
    try {
      const r = await partnerApi.claim(id, note);
      toast.success(r.message);
      onClose();
    } catch (e) {
      showError(e);
    }
  };
  return (
    <div className="ep-modal-bg" role="dialog" aria-modal="true" aria-labelledby="ep-cl" onClick={onClose}>
      <div className="ep-modal ep" style={{ padding: 24, minHeight: 0 }} onClick={(e) => e.stopPropagation()}>
        <h2 id="ep-cl">Claim a listing</h2>
        <p className="body small" style={{ marginTop: 6 }}>Find a program GradMap already lists that your organization runs. GradMap confirms it, usually within 2 business days.</p>
        <input className="in" autoFocus style={{ marginTop: 12 }} placeholder="Program or organization name" value={q} onChange={(e) => search(e.target.value)} aria-label="Search listings" />
        <div style={{ marginTop: 8, maxHeight: 260, overflow: 'auto' }}>
          {found.map((f) => (
            <div key={f.id} className="trow" style={{ padding: '10px 0' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{f.name}</div>
                <div className="muted small">{`#${f.id}${f.host ? ` · ${f.host}` : ''}`}</div>
              </div>
              {f.managed ? <span className="chip">Already managed</span> : <button type="button" className="btn sm" onClick={() => claim(f.id)}>Claim</button>}
            </div>
          ))}
          {q.trim().length >= 3 && found.length === 0 && <p className="muted small" style={{ marginTop: 8 }}>No listings match. You can add it as a new program instead.</p>}
        </div>
        <label className="lbl" htmlFor="ep-cn" style={{ marginTop: 12 }}>A note for GradMap (optional)</label>
        <input id="ep-cn" className="in" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} placeholder="e.g. we took over this program in 2025" />
        <div className="foot"><span /><button type="button" className="btn" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

function StatsPage() {
  const rows = useLoad<StatRow[]>(() => partnerApi.stats(), []);
  const n = (v: number | null) => (v == null ? 'Fewer than 5' : v.toLocaleString('en-US'));
  return (
    <>
      <h1 style={{ margin: '16px 0 4px' }}>Stats</h1>
      <p className="body" style={{ marginBottom: 16 }}>Last 30 days for views. Counts under 5 show as "Fewer than 5". GradMap never shares who the students are.</p>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="ep-tbl">
          <thead><tr><th>Program</th><th>Views in GradMap</th><th>Views on gradmap.com</th><th>Saved by students</th><th>Students applying</th></tr></thead>
          <tbody>
            {(rows.data ?? []).map((r) => (
              <tr key={r.id}>
                <td data-l="Program" style={{ fontWeight: 500 }}>{r.name}</td>
                <td data-l="Views in GradMap">{n(r.views_30d_app)}</td>
                <td data-l="Views on gradmap.com">{n(r.views_30d_public)}</td>
                <td data-l="Saved by students">{n(r.saves)}</td>
                <td data-l="Students applying">{n(r.applying)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function TeamPage({ me }: { me: PartnerMe }) {
  const team = useLoad<TeamRow[]>(() => partnerApi.team(), []);
  const [f, setF] = useState({ email: '', first_name: '', last_name: '' });
  const domain = (me.org.website ?? '').replace(/^https?:\/\/(www\.)?/i, '').split('/')[0];
  const add = async () => {
    try {
      await partnerApi.invite(f);
      toast.success(`Added. ${f.first_name} gets an email to set a password.`);
      setF({ email: '', first_name: '', last_name: '' });
      team.reload();
    } catch (e) {
      showError(e);
    }
  };
  return (
    <>
      <h1 style={{ margin: '16px 0 16px' }}>Team</h1>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="ep-tbl">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th></tr></thead>
          <tbody>
            {(team.data ?? []).map((t) => (
              <tr key={t.id}>
                <td data-l="Name" style={{ fontWeight: 500 }}>{[t.first_name, t.last_name].filter(Boolean).join(' ')}</td>
                <td data-l="Email">{t.email}</td>
                <td data-l="Role">{t.role === 'owner' ? 'Owner' : 'Editor'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {me.role === 'owner' ? (
        <section className="card" style={{ marginTop: 16 }}>
          <h2>Add a teammate</h2>
          <p className="body small" style={{ marginTop: 4 }}>{`Teammates need an email at your organization${domain ? ` (@${domain})` : ''}. They can edit programs; only you can add people.`}</p>
          <div className="ep-grid3" style={{ marginTop: 12 }}>
            <input className="in" placeholder="First name" aria-label="First name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} />
            <input className="in" placeholder="Last name" aria-label="Last name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} />
            <input className="in" type="email" placeholder="Work email" aria-label="Work email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          </div>
          <button type="button" className="btn primary" style={{ marginTop: 12 }} disabled={!f.email || !f.first_name} onClick={add}>Add teammate</button>
        </section>
      ) : (
        <p className="muted small" style={{ marginTop: 12 }}>Only the account owner can add teammates.</p>
      )}
    </>
  );
}
